import { REASONS_REQUIRING_ABSENCE } from 'utils/breakdown';

import { CUSHION_CHOICES, NODE_IDS } from '../const';
import { naiveToUtc } from './incidentTime';

/**
 * Maps controller data into the `createWorkAssignmentBreakdown` mutation input.
 *
 * Emits the common envelope + branch-specific optional blocks based on
 * `meta.previousNodeId` (the leaf just confirmed). Drops UI-only fields
 * (`parallelRideChosen`, `cushioningChoice`, `deadhead`, `parallelRide`,
 * `replacementDriver`, `replacementDriverId`, `segment`, `breakdownStation`,
 * `fromCityName`). The denormalized `*Object` fields feed Summary; the
 * payload emits only ids.
 *
 * `originalDriverAbsence` is collected on the common step whenever the reason
 * is in `REASONS_REQUIRING_ABSENCE` (currently `driverFatigue`) and is
 * forwarded to the mutation whenever it was collected — including the
 * parallel-ride branch, where the original driver is on absence and therefore
 * has no `outboundTraveling` back either.
 *
 * @param {Object} data Controller data (merged step values).
 * @param {{
 *   sequenceGroupId: string,
 *   originalDriverId: string,
 *   previousNodeId: string,
 * }} meta
 * @returns {Object} mutation input
 */
export const buildSubmitPayload = (data, meta) => {
	const { sequenceGroupId, originalDriverId, previousNodeId } = meta;
	const tz = data.incidentTimezone;
	const needsAbsence = REASONS_REQUIRING_ABSENCE.includes(data.breakdownReason);

	const payload = {
		workAssignmentSequenceGroupId: sequenceGroupId,
		reason: data.breakdownReason,
		incidentTimestamp: naiveToUtc(data.incidentTimestamp, tz),
		fromStationId: data.fromStationId,
		toStationId: data.toStationId,
		breakdownStationId: data.breakdownStationId,
	};

	if (data.note && String(data.note).trim().length > 0) {
		payload.note = data.note;
	}

	switch (previousNodeId) {
		case NODE_IDS.parallelRide:
		case NODE_IDS.parallelRideWithCushion: {
			payload.parallelRideId = data.parallelRideId;
			// Fatigue (and any other absence-requiring reason): the original
			// driver is unfit to work, so they don't travel back — emit
			// `originalDriverAbsence`, skip `outboundTraveling`. Non-absence
			// reasons keep the traveling block for the original driver.
			if (needsAbsence) {
				payload.originalDriverAbsence = {
					fromTimestamp: naiveToUtc(data.originalDriverAbsence.fromTimestamp, tz),
					reasonCode: data.originalDriverAbsence.reasonCode,
				};
			} else {
				payload.outboundTraveling = {
					fromTimestamp: naiveToUtc(data.outboundTraveling.fromTimestamp, tz),
					toTimestamp: naiveToUtc(data.outboundTraveling.toTimestamp, tz),
					crewMemberId: originalDriverId,
				};
			}
			break;
		}

		case NODE_IDS.busDeadhead: {
			payload.deadheadId = data.deadheadId;
			const cushion = data.cushioningChoice;
			const deadheadDriverId = data.deadhead?.crewMember?.id ?? null;
			const drivingDriverId =
				cushion === CUSHION_CHOICES.originalDriver ? deadheadDriverId : originalDriverId;
			const outboundDriverId =
				cushion === CUSHION_CHOICES.originalDriver ? originalDriverId : deadheadDriverId;
			payload.driving = {
				fromTimestamp: naiveToUtc(data.driving.fromTimestamp, tz),
				crewMemberId: drivingDriverId,
			};
			payload.outboundTraveling = {
				fromTimestamp: naiveToUtc(data.outboundTraveling.fromTimestamp, tz),
				toTimestamp: naiveToUtc(data.outboundTraveling.toTimestamp, tz),
				crewMemberId: outboundDriverId,
			};
			break;
		}

		case NODE_IDS.fatigueReplacement: {
			const replacementId = data.replacementDriverId;
			payload.originalDriverAbsence = {
				fromTimestamp: naiveToUtc(data.originalDriverAbsence.fromTimestamp, tz),
				reasonCode: data.originalDriverAbsence.reasonCode,
			};
			payload.driving = {
				fromTimestamp: naiveToUtc(data.driving.fromTimestamp, tz),
				crewMemberId: replacementId,
			};
			payload.replacementInboundTraveling = {
				fromCityId: data.replacementInboundTraveling.fromCityId,
				fromTimestamp: naiveToUtc(data.replacementInboundTraveling.fromTimestamp, tz),
				toTimestamp: naiveToUtc(data.replacementInboundTraveling.toTimestamp, tz),
				crewMemberId: replacementId,
			};
			break;
		}

		case NODE_IDS.driverOutOfHoursReplacement: {
			const replacementId = data.replacementDriverId;
			payload.driving = {
				fromTimestamp: naiveToUtc(data.driving.fromTimestamp, tz),
				crewMemberId: replacementId,
			};
			payload.replacementInboundTraveling = {
				fromCityId: data.replacementInboundTraveling.fromCityId,
				fromTimestamp: naiveToUtc(data.replacementInboundTraveling.fromTimestamp, tz),
				toTimestamp: naiveToUtc(data.replacementInboundTraveling.toTimestamp, tz),
				crewMemberId: replacementId,
			};
			payload.outboundTraveling = {
				fromTimestamp: naiveToUtc(data.outboundTraveling.fromTimestamp, tz),
				toTimestamp: naiveToUtc(data.outboundTraveling.toTimestamp, tz),
				crewMemberId: originalDriverId,
			};
			break;
		}

		default:
			throw new Error(
				`buildSubmitPayload: unknown previousNodeId "${previousNodeId}" — cannot build payload`
			);
	}

	return payload;
};
